// The portfolio: properties, their units, and the rules that actually apply to them.
//
// The screen that matters here is the property detail, and specifically the rule
// stack. A manager needs to see not just "this is a Chicago building" but which
// obligations that produces, which layer produced each one, and what has been
// exempted — because the answer differs between two buildings a block apart, and
// the cost of assuming is measured in months of rent.

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Modal, Input, Select, Textarea,
    Button, Notice, Loading, Empty, LayerChips, money, fmtDate, titleCase, statusTone,
} from '../../components/secure/pm/PMKit';

const YESNO = [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }];

export default function PMPortfolioPage() {
    const apiClient = useApiClient();
    const [params, setParams] = useSearchParams();
    const selectedId = params.get('property');

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [owners, setOwners] = useState([]);
    const [jurisdictions, setJurisdictions] = useState([]);
    const [detail, setDetail] = useState(null);
    const [labor, setLabor] = useState([]);
    const [tab, setTab] = useState('units');
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadList = useCallback(async () => {
        try {
            const [p, o, j] = await Promise.all([
                apiClient.get('/api/property-management/properties'),
                apiClient.get('/api/property-management/owners'),
                apiClient.get('/api/property-management/jurisdictions'),
            ]);
            setRows(p || []); setOwners(o || []); setJurisdictions(j || []);
        } catch (e) { setError({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient]);

    const loadDetail = useCallback(async (id) => {
        if (!id) { setDetail(null); return; }
        try {
            const [d, l] = await Promise.all([
                apiClient.get(`/api/property-management/properties/${id}`),
                apiClient.get(`/api/property-management/properties/${id}/labor-agreements`),
            ]);
            setDetail(d); setLabor(l || []);
        } catch (e) { setError({ message: e.message }); }
    }, [apiClient]);

    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

    const submit = async (path, body, after) => {
        setBusy(true); setError(null);
        try {
            const r = await apiClient.post(path, body);
            setModal(null); setForm({});
            if (r?.warning) setError({ tone: 'warn', title: 'Recorded, with a caveat', message: r.warning });
            await loadList(); if (selectedId) await loadDetail(selectedId);
            if (after) after(r);
        } catch (e) {
            setError({ message: e.message, problems: e.problems });
        } finally { setBusy(false); }
    };

    if (loading) return <Loading />;

    const municipal = jurisdictions.filter((j) => j.layer === 'municipal');
    const county = jurisdictions.filter((j) => j.layer === 'county');
    const eff = detail?.rule_stack?.effective || {};
    const prov = detail?.rule_stack?.provenance || {};

    // Where a rule came from is as load-bearing as the rule. Showing them together
    // is what makes a computed deadline defensible rather than mysterious.
    const Rule = ({ label, value, field, suffix }) => (
        <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
            <span className="text-xs text-slate-500">{label}</span>
            <span className="text-sm text-slate-800 font-medium text-right">
                {value === null || value === undefined
                    ? <span className="text-slate-300">not set</span>
                    : <>{value}{suffix}</>}
                {prov[field] && <Pill tone="indigo">{prov[field]}</Pill>}
            </span>
        </div>
    );

    return (
        <div>
            <PageHeader
                title="Portfolio"
                subtitle="Federal, state, county and municipal rules apply cumulatively. A municipal ordinance can displace the county layer; nothing displaces the state."
            >
                <Button onClick={() => { setForm({ state: 'IL', asset_class: 'residential', total_units: 1 }); setModal('property'); }}>
                    Add property
                </Button>
            </PageHeader>

            {error && <Notice tone={error.tone || 'bad'} title={error.title}
                message={error.message} problems={error.problems} onDismiss={() => setError(null)} />}

            <div className="grid lg:grid-cols-5 gap-5">
                <div className="lg:col-span-2">
                    <Panel title="Properties" subtitle={`${rows.length} on file`}>
                        <Table
                            columns={[
                                { key: 'name', label: 'Property',
                                    render: (r) => (
                                        <div>
                                            <div className="font-medium text-slate-800">{r.name}</div>
                                            <div className="text-xs text-slate-400">{r.city} · {r.unit_count} units</div>
                                        </div>
                                    ) },
                                { key: 'status', label: '', nowrap: true,
                                    render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                            ]}
                            rows={rows.map((r) => ({ ...r, _key: r.pm_property_id }))}
                            onRowClick={(r) => setParams({ property: r.pm_property_id })}
                            empty="No properties yet."
                        />
                    </Panel>
                </div>

                <div className="lg:col-span-3">
                    {!detail ? (
                        <Panel><Empty title="Select a property"
                            message="Its resolved rule stack, units, agreement and labour position appear here." /></Panel>
                    ) : (
                        <>
                            <Panel
                                title={detail.name}
                                subtitle={`${detail.street_address}, ${detail.city} ${detail.zip_code}`}
                                actions={<Pill tone={statusTone(detail.status)}>{titleCase(detail.status)}</Pill>}
                            >
                                <div className="grid sm:grid-cols-4 gap-3 mb-4">
                                    <Stat label="Units" value={detail.units.length} />
                                    <Stat label="Occupied"
                                        value={detail.units.filter((u) => u.status === 'occupied').length} />
                                    <Stat label="Class" value={titleCase(detail.asset_class)} />
                                    <Stat label="Built" value={detail.year_built || '—'}
                                        tone={detail.year_built && detail.year_built < 1978 ? 'warn' : 'default'}
                                        sub={detail.year_built && detail.year_built < 1978 ? 'lead paint applies' : ''} />
                                </div>
                                <div className="mb-3">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                                        Rule layers applied
                                    </div>
                                    <LayerChips layers={detail.rule_stack?.layers} />
                                </div>
                                {(detail.rule_stack?.exemptions || []).length > 0 && (
                                    <Notice tone="warn" title="Exempted"
                                        problems={detail.rule_stack.exemptions.map((e) => `${e.name}: ${e.reason}`)} />
                                )}
                            </Panel>

                            <div className="mt-5">
                                <Tabs
                                    tabs={[
                                        { key: 'units', label: 'Units', count: detail.units.length },
                                        { key: 'rules', label: 'Resolved rules' },
                                        { key: 'attachments', label: 'Lease attachments',
                                            count: (detail.rule_stack?.attachments || []).length },
                                        { key: 'labour', label: 'Building labour', count: labor.length },
                                    ]}
                                    active={tab} onChange={setTab}
                                />

                                {tab === 'units' && (
                                    <Panel actions={<Button variant="ghost"
                                        onClick={() => { setForm({ use_type: 'residential', status: 'vacant' }); setModal('unit'); }}>
                                        Add unit</Button>}>
                                        <Table
                                            columns={[
                                                { key: 'unit_number', label: 'Unit', nowrap: true },
                                                { key: 'use_type', label: 'Use', nowrap: true,
                                                    render: (r) => <Pill tone={r.use_type === 'commercial' ? 'violet' : 'slate'}>
                                                        {r.use_type}</Pill> },
                                                { key: 'beds', label: 'Beds / Baths',
                                                    render: (r) => r.bedrooms ? `${r.bedrooms} / ${r.bathrooms || '—'}` : '—' },
                                                { key: 'market_rent', label: 'Market rent', align: 'right',
                                                    render: (r) => money(r.market_rent) },
                                                { key: 'status', label: 'Status', nowrap: true,
                                                    render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                                            ]}
                                            rows={detail.units.map((u) => ({ ...u, _key: u.unit_id }))}
                                            empty="No units. A lease takes its type from the unit's use, so units come first."
                                        />
                                    </Panel>
                                )}

                                {tab === 'rules' && (
                                    <Panel subtitle="Each value shows the layer that set it. Where two layers speak, the more protective of the tenant governs.">
                                        <div className="grid sm:grid-cols-2 gap-x-8">
                                            <div>
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Deposit</h4>
                                                <Rule label="Cap" value={eff.deposit_cap_months} field="deposit_cap_months" suffix="× rent " />
                                                <Rule label="Itemise within" value={eff.deposit_itemize_days} field="deposit_itemize_days" suffix=" days " />
                                                <Rule label="Return within" value={eff.deposit_return_days} field="deposit_return_days" suffix=" days " />
                                                <Rule label="Interest required" value={Number(eff.deposit_interest_required) ? 'Yes' : 'No'} field="deposit_interest_required" suffix=" " />
                                                <Rule label="Bank disclosure" value={titleCase(eff.deposit_bank_disclosure)} field="deposit_bank_disclosure" suffix=" " />
                                                <Rule label="Escrow waiver" value={Number(eff.deposit_waiver_allowed) ? 'Permitted' : 'Prohibited'} field="deposit_waiver_allowed" suffix=" " />
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Notices &amp; fees</h4>
                                                <Rule label="Late fee model" value={titleCase(eff.late_fee_model)} field="late_fee_model" suffix=" " />
                                                <Rule label="Non-payment" value={eff.notice_nonpayment_days} field="notice_nonpayment_days" suffix=" days " />
                                                <Rule label="Lease violation" value={eff.notice_violation_days} field="notice_violation_days" suffix=" days " />
                                                <Rule label="Month-to-month" value={eff.notice_month_to_month_days} field="notice_month_to_month_days" suffix=" days " />
                                                <Rule label="Fair notice tier 1" value={eff.notice_tier_1_days} field="notice_tier_1_days" suffix=" days " />
                                                <Rule label="Fair notice tier 3" value={eff.notice_tier_3_days} field="notice_tier_3_days" suffix=" days " />
                                                <Rule label="Registration" value={Number(eff.registration_required) ? titleCase(eff.registration_period) : 'None'} field="registration_required" suffix=" " />
                                            </div>
                                        </div>
                                        {(detail.rule_stack?.preemptions || []).map((p) => (
                                            <p key={p.subject} className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-100">
                                                <strong className="text-slate-600">Preempted:</strong> {p.rule} <em>({p.authority})</em>
                                            </p>
                                        ))}
                                    </Panel>
                                )}

                                {tab === 'attachments' && (
                                    <Panel subtitle="What must accompany a written tenancy agreement here, and which layer requires it.">
                                        <Table
                                            columns={[
                                                { key: 'key', label: 'Document', render: (r) => titleCase(r.key) },
                                                { key: 'required_by', label: 'Required by', nowrap: true,
                                                    render: (r) => <Pill tone="indigo">{r.required_by}</Pill> },
                                                { key: 'layer', label: 'Layer', nowrap: true, render: (r) => titleCase(r.layer) },
                                            ]}
                                            rows={(detail.rule_stack?.attachments || []).map((a, i) => ({ ...a, _key: i }))}
                                            empty="Nothing beyond the base state requirements."
                                        />
                                    </Panel>
                                )}

                                {tab === 'labour' && (
                                    <Panel
                                        subtitle="ABOMA negotiates on behalf of member buildings with SEIU Local 1 and Teamsters Local 727. A building outside those agreements is not covered by them."
                                        actions={<Button variant="ghost"
                                            onClick={() => { setForm({ negotiated_by: 'aboma' }); setModal('labor'); }}>
                                            Record agreement</Button>}
                                    >
                                        <div className="grid sm:grid-cols-3 gap-3 mb-4">
                                            <Stat label="ABOMA member" value={detail.aboma_member ? 'Yes' : 'No'}
                                                tone={detail.aboma_member ? 'indigo' : 'default'} />
                                            <Stat label="Staff employer" value={titleCase(detail.building_staff_employer)} />
                                            <Stat label="Building type" value={titleCase(detail.building_type) || '—'} />
                                        </div>
                                        <Table
                                            columns={[
                                                { key: 'union_name', label: 'Union' },
                                                { key: 'unit_covered', label: 'Bargaining unit',
                                                    render: (r) => titleCase(r.unit_covered) },
                                                { key: 'term_end', label: 'Term ends', nowrap: true,
                                                    render: (r) => fmtDate(r.term_end) },
                                                { key: 'authorization_on_file', label: 'Authorization', nowrap: true,
                                                    render: (r) => r.authorization_on_file
                                                        ? <Pill tone="good">on file</Pill>
                                                        : <Pill tone="bad">missing</Pill> },
                                            ]}
                                            rows={labor.map((l) => ({ ...l, _key: l.labor_agreement_id }))}
                                            rowTone={(r) => (r.authorization_on_file ? null : 'warn')}
                                            empty="No collective bargaining agreements recorded for this building."
                                        />
                                    </Panel>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {modal === 'property' && (
                <Modal title="Add a property" subtitle="An owner is required — a property with no owner has no book to post to."
                    onClose={() => setModal(null)} wide>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Input label="Name" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <Select label="Owner" value={form.owner_id || ''}
                            onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                            options={[{ value: '', label: 'Select…' },
                                ...owners.map((o) => ({ value: o.owner_id, label: o.legal_name }))]} />
                        <Input label="Street address" value={form.street_address || ''}
                            onChange={(e) => setForm({ ...form, street_address: e.target.value })} className="sm:col-span-2" />
                        <Input label="City" value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                        <Input label="ZIP" value={form.zip_code || ''} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
                        <Select label="Asset class" value={form.asset_class}
                            onChange={(e) => setForm({ ...form, asset_class: e.target.value })}
                            options={['residential', 'commercial', 'mixed']}
                            hint="Mixed is common here — a three-flat over a storefront. The unit's use decides each lease." />
                        <Input label="Total units" type="number" value={form.total_units || ''}
                            onChange={(e) => setForm({ ...form, total_units: e.target.value })}
                            hint="With owner occupancy, decides the RTLO exemption." />
                        <Input label="Year built" type="number" value={form.year_built || ''}
                            onChange={(e) => setForm({ ...form, year_built: e.target.value })}
                            hint="Before 1978 triggers lead-based paint." />
                        <Select label="Owner occupied" value={form.owner_occupied ?? 0}
                            onChange={(e) => setForm({ ...form, owner_occupied: Number(e.target.value) })} options={YESNO} />
                        <Select label="Municipality" value={form.municipal_jurisdiction_id || ''}
                            onChange={(e) => setForm({ ...form, municipal_jurisdiction_id: e.target.value || null })}
                            options={[{ value: '', label: 'None — state law only' },
                                ...municipal.map((j) => ({ value: j.jurisdiction_id, label: j.name }))]} />
                        <Select label="County layer" value={form.county_jurisdiction_id || ''}
                            onChange={(e) => setForm({ ...form, county_jurisdiction_id: e.target.value || null })}
                            options={[{ value: '', label: 'None' },
                                ...county.map((j) => ({ value: j.jurisdiction_id, label: j.name }))]}
                            hint="Ignored where the municipality runs its own ordinance." />
                        <Select label="ABOMA member" value={form.aboma_member ?? 0}
                            onChange={(e) => setForm({ ...form, aboma_member: Number(e.target.value) })} options={YESNO} />
                        <Select label="Building staff employer" value={form.building_staff_employer || 'none'}
                            onChange={(e) => setForm({ ...form, building_staff_employer: e.target.value })}
                            options={['none', 'owner', 'brokerage', 'contractor']} />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => submit('/api/property-management/properties', form)}>
                            {busy ? 'Saving…' : 'Add property'}</Button>
                    </div>
                </Modal>
            )}

            {modal === 'unit' && (
                <Modal title={`Add a unit to ${detail?.name}`} onClose={() => setModal(null)}>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Input label="Unit number" value={form.unit_number || ''}
                            onChange={(e) => setForm({ ...form, unit_number: e.target.value })} />
                        <Select label="Use" value={form.use_type}
                            onChange={(e) => setForm({ ...form, use_type: e.target.value })}
                            options={['residential', 'commercial']}
                            hint="This decides which body of law governs the lease." />
                        <Input label="Bedrooms" type="number" value={form.bedrooms || ''}
                            onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} />
                        <Input label="Bathrooms" type="number" step="0.5" value={form.bathrooms || ''}
                            onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} />
                        <Input label="Square feet" type="number" value={form.square_feet || ''}
                            onChange={(e) => setForm({ ...form, square_feet: e.target.value })} />
                        <Input label="Market rent" type="number" step="0.01" value={form.market_rent || ''}
                            onChange={(e) => setForm({ ...form, market_rent: e.target.value })} />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => submit(`/api/property-management/properties/${selectedId}/units`, form)}>
                            {busy ? 'Saving…' : 'Add unit'}</Button>
                    </div>
                </Modal>
            )}

            {modal === 'labor' && (
                <Modal title="Record a collective bargaining agreement"
                    subtitle="ABOMA needs a signed labour authorization from the building before it may negotiate on that building's behalf."
                    onClose={() => setModal(null)}>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Select label="Negotiated by" value={form.negotiated_by}
                            onChange={(e) => setForm({ ...form, negotiated_by: e.target.value })}
                            options={['aboma', 'direct', 'other']} />
                        <Input label="Union" value={form.union_name || ''}
                            onChange={(e) => setForm({ ...form, union_name: e.target.value })}
                            hint="e.g. SEIU Local 1, Teamsters Local 727" />
                        <Select label="Bargaining unit" value={form.unit_covered || ''}
                            onChange={(e) => setForm({ ...form, unit_covered: e.target.value })}
                            options={[{ value: '', label: 'Select…' }, 'high_rise_janitorial',
                                'walk_up_janitorial', 'door_staff', 'garage', 'other']} />
                        <Select label="Authorization on file" value={form.authorization_on_file ?? 0}
                            onChange={(e) => setForm({ ...form, authorization_on_file: Number(e.target.value) })}
                            options={YESNO} />
                        <Input label="Term starts" type="date" value={form.term_start || ''}
                            onChange={(e) => setForm({ ...form, term_start: e.target.value })} />
                        <Input label="Term ends" type="date" value={form.term_end || ''}
                            onChange={(e) => setForm({ ...form, term_end: e.target.value })} />
                    </div>
                    <Textarea label="Notes" value={form.notes || ''}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => submit(`/api/property-management/properties/${selectedId}/labor-agreements`, form)}>
                            {busy ? 'Saving…' : 'Record'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
